import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';

export default function Dashboard() {
  const { user, api, navigate } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomePhone, setWelcomePhone] = useState('');
  const [welcomePhoneDigits, setWelcomePhoneDigits] = useState('');
  const [welcomeSmsConsent, setWelcomeSmsConsent] = useState(false);
  const [welcomeSaving, setWelcomeSaving] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const data = await api('/advisors/dashboard');
      setDashboardData(data);
      
      // Show welcome modal if onboarding not complete
      if (!data.advisor.onboardingComplete) {
        setShowWelcome(true);
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Phone formatting for welcome modal
  const formatPhoneDisplay = (digits) => {
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleWelcomePhoneChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setWelcomePhoneDigits('');
      setWelcomePhone('');
      return;
    }
    const allDigits = raw.replace(/\D/g, '');
    if (raw.length < welcomePhone.length) {
      const newDigits = welcomePhoneDigits.slice(0, -1);
      setWelcomePhoneDigits(newDigits);
      setWelcomePhone(formatPhoneDisplay(newDigits));
      return;
    }
    let updatedDigits = welcomePhoneDigits;
    if (allDigits.length > welcomePhoneDigits.length) {
      const added = allDigits.slice(welcomePhoneDigits.length);
      updatedDigits = (welcomePhoneDigits + added).slice(0, 10);
    }
    setWelcomePhoneDigits(updatedDigits);
    setWelcomePhone(formatPhoneDisplay(updatedDigits));
  };

  const handleWelcomeSave = async () => {
    setWelcomeSaving(true);
    try {
      const cleanPhone = welcomePhoneDigits.length === 10 ? `+1${welcomePhoneDigits}` : '';
      await api('/advisors/complete-onboarding', {
        method: 'POST',
        body: JSON.stringify({
          phone: cleanPhone,
          smsConsent: welcomeSmsConsent
        })
      });
      setShowWelcome(false);
      loadDashboard();
    } catch (error) {
      console.error('Failed to save onboarding:', error);
    } finally {
      setWelcomeSaving(false);
    }
  };

  const handleWelcomeSkip = async () => {
    try {
      await api('/advisors/complete-onboarding', {
        method: 'POST',
        body: JSON.stringify({})
      });
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
    }
    setShowWelcome(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-coral-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-navy-600 font-body">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const { advisor, stats, recentLeads, transactions } = dashboardData;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Welcome Onboarding Modal */}
      {showWelcome && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 sm:p-8">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-coral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="font-display text-2xl text-navy-900 mb-2">Welcome! Let's Get You Set Up</h2>
                <p className="text-navy-600 font-body text-sm">Just a couple of quick things before you get started.</p>
              </div>

              {/* Safe Senders */}
              <div className="bg-cream-50 rounded-lg p-4 mb-6 border border-cream-200">
                <h3 className="font-display text-base text-navy-900 mb-2">
                  Step 1: Add Us to Your Safe Senders
                </h3>
                <p className="text-navy-600 font-body text-sm mb-2">
                  Your leads will be delivered by email. To make sure they don't end up in spam, please add this address to your safe senders list:
                </p>
                <p className="font-mono text-navy-900 font-bold text-sm bg-white rounded px-3 py-2 border border-cream-200">
                  leads@thedeolixacademy.com
                </p>
              </div>

              {/* SMS Setup */}
              <div className="bg-cream-50 rounded-lg p-4 mb-6 border border-cream-200">
                <h3 className="font-display text-base text-navy-900 mb-3">
                  Step 2: Set Up SMS Notifications
                </h3>
                <p className="text-navy-600 font-body text-sm mb-3">
                  Get a text message every time a lead is assigned to you so you never miss one.
                </p>

                {/* Phone Input */}
                <div className="mb-3">
                  <label className="block text-xs font-medium text-navy-700 mb-1">
                    Mobile Phone Number
                  </label>
                  <input
                    type="tel"
                    value={welcomePhone}
                    onChange={handleWelcomePhoneChange}
                    placeholder="(555) 123-4567"
                    className="w-full px-3 py-2 border border-cream-300 rounded-lg text-sm focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                  />
                  <p className="text-xs text-navy-500 mt-1">US or Canadian mobile number.</p>
                </div>

                {/* Consent Checkbox */}
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={welcomeSmsConsent}
                    onChange={(e) => setWelcomeSmsConsent(e.target.checked)}
                    className="w-4 h-4 text-coral-500 rounded focus:ring-coral-500 mt-0.5"
                  />
                  <span className="ml-2 text-navy-700 text-xs font-body">
                    Yes, I would like to receive automated text messages from The Deolix Academy notifying me when a new lead has been assigned to my account. I understand I will receive one text message for each lead I receive.
                  </span>
                </label>
                <div className="mt-2 text-xs text-navy-400 font-body">
                  <p>Msg & data rates may apply. Reply HELP for help, STOP to opt out.</p>
                  <p className="mt-1">
                    <a href="https://join.thedeolixacademy.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-coral-500 underline">Terms</a>
                    {' | '}
                    <a href="https://join.thedeolixacademy.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-coral-500 underline">Privacy</a>
                  </p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleWelcomeSave}
                  disabled={welcomeSaving}
                  className="w-full py-3 bg-coral-500 hover:bg-coral-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {welcomeSaving ? 'Saving...' : 'Save & Continue'}
                </button>
                <button
                  onClick={handleWelcomeSkip}
                  className="w-full py-2 text-navy-500 hover:text-navy-700 text-sm font-body transition-colors"
                >
                  Skip for now (you can set this up later in Settings)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="font-display text-3xl text-navy-900 mb-2">
          Welcome back, {advisor.firstName}!
        </h1>
        <p className="text-navy-600 font-body">Here's your lead program overview</p>
      </div>

      {/* Status Banner */}
      {advisor.isPaused && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-amber-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-amber-800 font-body">Lead delivery is currently paused</span>
          </div>
          <button onClick={() => navigate('/settings')} className="text-amber-700 font-medium hover:text-amber-800">
            Adjust Settings
          </button>
        </div>
      )}

      {/* Stats Cards - 2 cards */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-navy-500 text-sm uppercase tracking-wide font-body">Available Leads</span>
            <div className="w-10 h-10 bg-gold-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-gold-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="text-3xl font-display text-navy-900">{stats.remainingLeads}</p>
          <p className="text-navy-500 text-sm mt-1 font-body">leads in balance</p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-navy-500 text-sm uppercase tracking-wide font-body">Leads Received</span>
            <div className="w-10 h-10 bg-highlight-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-highlight-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
          <p className="text-3xl font-display text-navy-900">{stats.totalLeadsReceived}</p>
          <p className="text-navy-500 text-sm mt-1 font-body">total leads received</p>
        </div>
      </div>

      {/* Actions */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="card flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg text-navy-900 mb-1">Purchase More Leads</h3>
            <p className="text-navy-600 text-sm font-body">Add to your lead balance with our prepaid packages</p>
          </div>
          <button onClick={() => navigate('/purchase')} className="btn-primary">
            Buy Leads
          </button>
        </div>

        <div className="card flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg text-navy-900 mb-1">Lead Delivery Settings</h3>
            <p className="text-navy-600 text-sm font-body">
              Adjust how many leads per week you receive
            </p>
          </div>
          <button 
            onClick={() => navigate('/settings')}
            className="btn-secondary flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Settings
          </button>
        </div>
      </div>

      {/* Recent Leads */}
      <div className="card mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-xl text-navy-900">Recent Leads</h2>
          <button onClick={() => navigate('/my-leads')} className="text-coral-600 hover:text-coral-700 text-sm font-medium">
            View All →
          </button>
        </div>

        {recentLeads.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-cream-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-navy-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h3 className="font-display text-lg text-navy-900 mb-2">No leads yet</h3>
            <p className="text-navy-600 mb-4 font-body">Purchase a lead package to start receiving leads</p>
            <button onClick={() => navigate('/purchase')} className="btn-primary">
              Purchase Leads
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-cream-200">
                  <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-3 px-4">Name</th>
                  <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-3 px-4">Email</th>
                  <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-3 px-4">Phone</th>
                  <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-3 px-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {recentLeads.map((lead) => (
                  <tr key={lead.id} className="border-b border-cream-100 hover:bg-cream-50">
                    <td className="py-4 px-4 font-medium text-navy-900">{lead.name}</td>
                    <td className="py-4 px-4">
                      <a href={`mailto:${lead.email}`} className="text-coral-600 hover:text-coral-700">
                        {lead.email}
                      </a>
                    </td>
                    <td className="py-4 px-4 text-navy-600 font-body">
                      {lead.phone ? (
                        <a href={`tel:${lead.phone}`} className="hover:text-navy-900">{lead.phone}</a>
                      ) : (
                        <span className="text-navy-400">—</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-navy-500 text-sm font-body">
                      {new Date(lead.assigned_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent Transactions */}
      {transactions.length > 0 && (
        <div className="card">
          <h2 className="font-display text-xl text-navy-900 mb-6">Recent Transactions</h2>
          <div className="space-y-4">
            {transactions.slice(0, 5).map((tx) => (
              <div key={tx.id} className="flex items-center justify-between py-3 border-b border-cream-100 last:border-0">
                <div>
                  <p className="font-medium text-navy-900">
                    {tx.leads_purchased} lead{tx.leads_purchased > 1 ? 's' : ''} package
                  </p>
                  <p className="text-sm text-navy-500 font-body">
                    {tx.delivery_weeks} week delivery • {new Date(tx.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-navy-900">${(tx.amount_cents / 100).toFixed(0)}</p>
                  <span className={`badge ${tx.status === 'completed' ? 'badge-success' : 'badge-warning'}`}>
                    {tx.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
